import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  Package,
  CheckCircle,
  XCircle
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import assetTypeMasterService from '../../services/assetTypeMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const AssetTypeMaster = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalTypes: 0,
    activeTypes: 0,
    inactiveTypes: 0,
    byCategory: {}
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [typesResponse, statsResponse] = await Promise.all([
        assetTypeMasterService.getAllAssetTypes({ limit: 1000 }),
        assetTypeMasterService.getAssetTypeStats()
      ])
      
      if (typesResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (typesResponse.data || []).map(type => ({
          ...type,
          id: type._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (typesResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No asset type records found. Create your first asset type!')
        }
      } else {
        toast.error(typesResponse.message || 'Failed to load asset types')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading asset type master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view asset types')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to load asset type master records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      accessorKey: 'assetTypeCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'assetTypeName',
      header: 'Asset Type Name',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'assetCategory',
      header: 'Category',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>
          <span className="status-badge active">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'parentAssetType',
      header: 'Parent Type',
      cell: ({ row }) => {
        const parent = row.original.parentAssetType
        return (
          <div style={{ textAlign: 'left' }}>
            {parent ? (parent.assetTypeName || parent.assetTypeCode || '-') : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'depreciation.method',
      header: 'Depreciation Method',
      cell: ({ row }) => {
        const method = row.original.depreciation?.method || 'None'
        return (
          <div style={{ textAlign: 'left' }}>
            {method}
          </div>
        )
      },
    },
    {
      accessorKey: 'depreciation.usefulLife',
      header: 'Useful Life (Years)',
      cell: ({ row }) => {
        const life = row.original.depreciation?.usefulLife || 0
        return (
          <div style={{ textAlign: 'right' }}>
            {life > 0 ? life : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getValue()?.toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/assets/asset-type-master/form')
  }

  const handleEdit = (typeData) => {
    navigate('/assets/asset-type-master/edit', { state: { typeData } })
  }

  const handleView = (typeData) => {
    navigate('/assets/asset-type-master/view', { state: { typeData } })
  }

  const handleDelete = (typeData) => {
    setDeleteItem(typeData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting asset type...')
      const response = await assetTypeMasterService.deleteAssetType(deleteItem._id)
      if (response.success) {
        toast.success('Asset type deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete asset type')
      }
    } catch (error) {
      console.error('Error deleting asset type:', error)
      if (error.response?.status === 404) {
        toast.error('Asset type not found')
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Cannot delete asset type with child types')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this asset type')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to delete asset type. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Assets', path: '/assets' },
          { label: 'Asset Types', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Asset Types</h1>
          <p>Asset categories and classifications</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Asset Type
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <Package size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalTypes || 0}</div>
          <div className="metric-title">Total Types</div>
          <div className="metric-subtitle">All asset types</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeTypes || 0}</div>
          <div className="metric-title">Active Types</div>
          <div className="metric-subtitle">Currently in use</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <XCircle size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Inactive</span>
            </div>
          </div>
          <div className="metric-value">{stats.inactiveTypes || 0}</div>
          <div className="metric-title">Inactive Types</div>
          <div className="metric-subtitle">Not in use</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          searchable={true}
          searchPlaceholder="Search asset types..."
          noCard={true}
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Asset Type"
        message={`Are you sure you want to delete "${deleteItem?.assetTypeName || deleteItem?.assetTypeCode}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default AssetTypeMaster

